import {
   Text,
   Box,
   Popover,
   PopoverContent,
   PopoverArrow,
   PopoverBody,
   Button,
   Flex,
   ButtonProps,
   usePopoverContext,
   VStack,
   HStack,
} from '@chakra-ui/react';
import { FaIcon } from '@ifixit/icons';
import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye';
import { faMessages } from '@fortawesome/pro-regular-svg-icons/faMessages';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { faStar } from '@fortawesome/pro-regular-svg-icons/faStar';
import {
   faStar as faFilledStar,
   faCircleCheck as faFilledCircleCheck,
   IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { _js, ___p } from '@ifixit/localize';
import { useContext, MouseEventHandler, SVGProps } from 'react';
import { GuideFavoriteContext } from './GuideFavorite';
import { GuideCompletionContext } from './GuideComplete';
import { HoverPopoverTrigger } from './HoverPopoverTrigger';
import { GuideData } from './GuideHeader';
import { localizeAndFormat } from 'Shared/utils/numberFormat';

export function GuideStats({
   isTeardown,
   guideCommentCount,
   stepCommentCount,
   viewCounts,
}: Pick<GuideData, 'isTeardown' | 'guideCommentCount' | 'stepCommentCount' | 'viewCounts'>) {
   const Separator = <Box width="1px" height="20px" borderColor="gray.400" />;

   return (
      <HStack divider={Separator} spacing="12px">
         <ViewStat viewCounts={viewCounts} />
         <PageCommentsStat
            guideCommentCount={guideCommentCount}
            stepCommentCount={stepCommentCount}
         />
         <CompletionStat />
         {!isTeardown && <FavoriteStat isTeardown={isTeardown} />}
      </HStack>
   );
}

const textProps = {
   display: 'flex',
   sx: { '& svg': { paddingRight: '4px' } },
   fontWeight: 'medium',
   margin: '0px',
   color: 'gray.500',
   _visited: { color: 'gray.500' },
   _hover: { textDecoration: 'none', color: 'gray.700' },
};

function ViewStat({ viewCounts }: Pick<GuideData, 'viewCounts'>) {
   return (
      <Popover trigger="hover">
         <HoverPopoverTrigger>
            <Button variant="link" {...textProps} tabIndex={0}>
               <FaIcon alignSelf="center" fontSize="16px" icon={faEye} color="gray.700" />
               {localizeAndFormat(viewCounts.all, { notation: 'compact' })}
            </Button>
         </HoverPopoverTrigger>
         <PopoverContent>
            <PopoverArrow backgroundColor="gray.800" />
            <PopoverBody
               borderRadius="md"
               backgroundColor="gray.800"
               color="white"
               fontSize="14px"
               padding="12px"
            >
               <VStack width="100%" spacing="8px">
                  <HStack alignSelf="start" spacing="4px">
                     <StatsIcon width="20" height="20" />
                     <Text color="gray.300" fontSize="14px" fontWeight={400}>
                        {_js('View statistics:')}
                     </Text>
                  </HStack>
                  <ViewStatSection
                     text={_js('Past 24 Hours:')}
                     stat={localizeAndFormat(viewCounts.today)}
                  />
                  <ViewStatSection
                     text={_js('Past 7 Days:')}
                     stat={localizeAndFormat(viewCounts.week)}
                  />
                  <ViewStatSection
                     text={_js('Past 30 Days:')}
                     stat={localizeAndFormat(viewCounts.month)}
                  />
                  <ViewStatSection
                     text={_js('All Time:')}
                     stat={localizeAndFormat(viewCounts.all)}
                  />
               </VStack>
            </PopoverBody>
         </PopoverContent>
      </Popover>
   );
}

function PageCommentsStat({
   guideCommentCount,
   stepCommentCount,
}: Pick<GuideData, 'guideCommentCount' | 'stepCommentCount'>) {
   return (
      <Popover trigger="hover">
         <CommentsPopoverContent
            guideCommentCount={guideCommentCount}
            stepCommentCount={stepCommentCount}
         />
      </Popover>
   );
}

function CommentsPopoverContent({
   guideCommentCount,
   stepCommentCount,
}: Pick<GuideData, 'guideCommentCount' | 'stepCommentCount'>) {
   const { isOpen, onClose } = usePopoverContext();

   const pageCommentsEl = document.querySelector('.js-page-comments');
   const onClickComments = () => {
      onClose();
      pageCommentsEl?.scrollIntoView({ behavior: 'smooth' });
   };
   const totalCount = guideCommentCount + stepCommentCount;
   // TODO: fix text and button behavior
   const text = getCommentText(guideCommentCount, stepCommentCount);
   // Show the button to the guide comments if there are guide comments, or there are no comments at all.
   // Do not show if there are step comments only.
   const showGuideCommentsButton = guideCommentCount > 0 || totalCount == 0;

   const onClick = isOpen ? onClickComments : () => {};

   return (
      <>
         <HoverPopoverTrigger>
            <Button variant="link" {...textProps} tabIndex={0}>
               <FaIcon fontSize="16px" icon={faMessages} color="gray.700" />
               {totalCount}
            </Button>
         </HoverPopoverTrigger>
         {showGuideCommentsButton ? (
            <TextButtonContent
               text={text}
               icon={faMessages}
               buttonText={_js('Go to guide comments')}
               onButtonClick={onClick}
            />
         ) : (
            <TextContent text={text} />
         )}
      </>
   );
}
function getCommentText(guideCommentCount: number, stepCommentCount: number): string {
   const hasGuideComment = guideCommentCount > 0;
   const hasStepComment = stepCommentCount > 0;

   if (!hasGuideComment && !hasStepComment) {
      return _js('No one has left comments on this guide. Will you be first?');
   }

   const guideCommentText = hasGuideComment
      ? ___p(
           guideCommentCount,
           '%1 guide comment.',
           '%1 guide comments.',
           guideCommentCount.toString()
        )
      : '';
   const stepCommentText = ___p(
      stepCommentCount,
      '%1 comment on steps',
      '%1 comments on steps',
      stepCommentCount.toString()
   );

   if (hasGuideComment && !hasStepComment) {
      return guideCommentText;
   }

   if (hasStepComment && !hasGuideComment) {
      return stepCommentText + '.';
   }

   return stepCommentText + ', ' + guideCommentText;
}
function CompletionStat() {
   const guideCompletionContext = useContext(GuideCompletionContext);

   const onClick = () =>
      guideCompletionContext.isCompleted
         ? guideCompletionContext.removeComplete(App.guideid)
         : guideCompletionContext.complete(App.guideid);
   const icon = guideCompletionContext.isCompleted ? faFilledCircleCheck : faCircleCheck;

   const noCompletionsText = _js(
      'No one has marked this guide "completed." Will you be the first?'
   );

   const completedText = ___p(
      guideCompletionContext.completedCount,
      '%1 person has marked this guide "completed." It\'s you — great job!',
      '%1 people have marked this guide "completed." Including you — great job!',
      guideCompletionContext.completedCount.toString()
   );

   const notCompletedText = ___p(
      guideCompletionContext.completedCount,
      '%1 person has marked this guide "completed." Will you be next?',
      '%1 people have marked this guide "completed." Will you be next?',
      guideCompletionContext.completedCount.toString()
   );

   const text =
      guideCompletionContext.completedCount == 0
         ? noCompletionsText
         : guideCompletionContext.isCompleted
           ? completedText
           : notCompletedText;

   const buttonText = guideCompletionContext.isCompleted
      ? _js('Remove completion')
      : _js('Mark complete');

   return (
      <Popover trigger="hover">
         <HoverPopoverTrigger>
            <Button variant="link" {...textProps} tabIndex={0}>
               <FaIcon fontSize="16px" icon={icon} color="green.500" />
               {guideCompletionContext.completedCount}
            </Button>
         </HoverPopoverTrigger>
         <TextButtonContent
            text={text}
            buttonText={buttonText}
            icon={icon}
            onButtonClick={onClick}
         />
      </Popover>
   );
}

function FavoriteStat({ isTeardown }: Pick<GuideData, 'isTeardown'>) {
   const guideFavoriteContext = useContext(GuideFavoriteContext);
   const onClick = () => {
      if (guideFavoriteContext.isFavorite) {
         guideFavoriteContext.unfavorite(App.guideid, App.langid, isTeardown);
      } else {
         guideFavoriteContext.favorite(App.guideid, App.langid, isTeardown);
      }
   };
   const icon = guideFavoriteContext.isFavorite ? faFilledStar : faStar;

   const noFavoritesText = _js(
      'No one has favorited (and subscribed to) this guide. Will you be the first?'
   );

   const favoritedText = ___p(
      guideFavoriteContext.favoriteCount,
      "%1 person has favorited (and subscribed to) this guide — and that's you!",
      '%1 people have favorited (and subscribed to) this guide — including you!',
      guideFavoriteContext.favoriteCount.toString()
   );

   const notFavoritedText = ___p(
      guideFavoriteContext.favoriteCount,
      '%1 person has favorited (and subscribed to) this guide. Will you be next?',
      '%1 people have favorited (and subscribed to) this guide. Will you be next?',
      guideFavoriteContext.favoriteCount.toString()
   );

   const text =
      guideFavoriteContext.favoriteCount == 0
         ? noFavoritesText
         : guideFavoriteContext.isFavorite
           ? favoritedText
           : notFavoritedText;

   const buttonText = guideFavoriteContext.isFavorite
      ? _js('Remove favorite')
      : _js('Add favorite');
   return (
      <Popover trigger="hover">
         <HoverPopoverTrigger>
            <Button variant="link" {...textProps} tabIndex={0}>
               <FaIcon fontSize="16px" icon={icon} color="yellow.500" />
               {guideFavoriteContext.favoriteCount}
            </Button>
         </HoverPopoverTrigger>
         <TextButtonContent
            text={text}
            onButtonClick={onClick}
            buttonText={buttonText}
            icon={icon}
         />
      </Popover>
   );
}

function TextContent({ text }: { text: string }) {
   return (
      <PopoverContent>
         <PopoverArrow backgroundColor="gray.800" />
         <PopoverBody borderRadius="md" backgroundColor="gray.800" display="grid">
            <Text color="white" fontSize="14px" whiteSpace="pre-line">
               {text}
            </Text>
         </PopoverBody>
      </PopoverContent>
   );
}

function TextButtonContent({
   icon,
   text,
   buttonText,
   onButtonClick,
}: ButtonProps & {
   icon: IconDefinition;
   text: string;
   buttonText: string;
   onButtonClick: MouseEventHandler<HTMLButtonElement>;
}) {
   return (
      <PopoverContent>
         <PopoverArrow backgroundColor="gray.800" />
         <PopoverBody borderRadius="md" backgroundColor="gray.800" display="grid">
            <Text color="white" fontSize="14px" whiteSpace="pre-line">
               {text}
            </Text>
            <Flex width="100%" justifyContent="flex-end">
               <StyledButton
                  icon={icon}
                  flexShrink={1}
                  justifyContent="flex-end"
                  onClick={onButtonClick}
               >
                  {buttonText}
               </StyledButton>
            </Flex>
         </PopoverBody>
      </PopoverContent>
   );
}

function StyledButton({ children, icon, ...props }: ButtonProps & { icon?: IconDefinition }) {
   return (
      <Button variant="cta" mt={2} {...props}>
         {icon && <FaIcon fontSize="16px" icon={icon} color="white" paddingRight="8px" />}
         {children}
      </Button>
   );
}

function StatsIcon(props: SVGProps<SVGSVGElement>) {
   return (
      <svg
         width="20"
         height="20"
         viewBox="0 0 20 20"
         fill="none"
         xmlns="http://www.w3.org/2000/svg"
         {...props}
      >
         <path
            d="M16.625 2.78125C17.0312 2.4375 17.125 1.8125 16.7812 1.375C16.4375 0.96875 15.7812 0.875 15.375 1.21875L10.9688 4.75L7.59375 2.21875C7.21875 1.9375 6.71875 1.9375 6.375 2.21875L1.375 6.21875C0.9375 6.5625 0.875 7.21875 1.21875 7.625C1.5625 8.0625 2.1875 8.125 2.625 7.78125L7 4.28125L10.375 6.8125C10.75 7.09375 11.25 7.09375 11.625 6.78125L16.625 2.78125ZM6 8V14C6 14.5625 6.4375 15 7 15C7.53125 15 8 14.5625 8 14V8C8 7.46875 7.53125 7 7 7C6.4375 7 6 7.46875 6 8ZM2 11V14C2 14.5625 2.4375 15 3 15C3.53125 15 4 14.5625 4 14V11C4 10.4688 3.53125 10 3 10C2.4375 10 2 10.4688 2 11ZM11 9C10.4375 9 10 9.46875 10 10V14C10 14.5625 10.4375 15 11 15C11.5312 15 12 14.5625 12 14V10C12 9.46875 11.5312 9 11 9ZM14 8V14C14 14.5625 14.4375 15 15 15C15.5312 15 16 14.5625 16 14V8C16 7.46875 15.5312 7 15 7C14.4375 7 14 7.46875 14 8Z"
            fill="#6B7280"
         />
      </svg>
   );
}

function ViewStatSection({ text, stat }: { text: string; stat: string }) {
   return (
      <Flex background="gray.700" borderRadius="4px" width="100%">
         <Text color="gray.300" fontSize="12px" fontWeight={400} marginLeft="12px" flexGrow={1}>
            {text}
         </Text>
         <Text color="gray.100" fontSize="14px" fontWeight={590} marginRight="12px">
            {stat}
         </Text>
      </Flex>
   );
}
